import Constants from 'expo-constants';

const expoExtra = (Constants.expoConfig?.extra || (Constants as any).manifest?.extra || {}) as Record<string, unknown>;

const directEnv = {
  EXPO_PUBLIC_ENV: process.env.EXPO_PUBLIC_ENV,
  EXPO_PUBLIC_API_URL: process.env.EXPO_PUBLIC_API_URL,
  EXPO_PUBLIC_SKIP_OFFLINE_CACHE: process.env.EXPO_PUBLIC_SKIP_OFFLINE_CACHE,
  EXPO_PUBLIC_ENABLE_DETOX_CONTROLS: process.env.EXPO_PUBLIC_ENABLE_DETOX_CONTROLS,
  EXPO_PUBLIC_MOCK_NATIVE_SHARING: process.env.EXPO_PUBLIC_MOCK_NATIVE_SHARING,
  GOOGLE_PLACES_API_KEY: process.env.GOOGLE_PLACES_API_KEY,
};

type RuntimeConfigKey = keyof typeof directEnv;

const readRuntimeConfigValue = (key: RuntimeConfigKey): string | undefined => {
  const value = expoExtra[key] ?? directEnv[key];
  return typeof value === 'string' ? value : undefined;
};

// Import from @env module (react-native-dotenv)
// These may be undefined if not set in .env file
let EXPO_PUBLIC_ENV_VALUE: string | undefined;
let EXPO_PUBLIC_API_URL_VALUE: string | undefined;
let EXPO_PUBLIC_SKIP_OFFLINE_CACHE_VALUE: string | undefined;
let EXPO_PUBLIC_ENABLE_DETOX_CONTROLS_VALUE: string | undefined;
let EXPO_PUBLIC_MOCK_NATIVE_SHARING_VALUE: string | undefined;
let GOOGLE_PLACES_API_KEY_VALUE: string | undefined;
try {
  // Dynamic require to handle cases where @env module doesn't exist
  const env = require('@env');
  EXPO_PUBLIC_ENV_VALUE = env.EXPO_PUBLIC_ENV || readRuntimeConfigValue('EXPO_PUBLIC_ENV');
  EXPO_PUBLIC_API_URL_VALUE = env.EXPO_PUBLIC_API_URL || readRuntimeConfigValue('EXPO_PUBLIC_API_URL');
  EXPO_PUBLIC_SKIP_OFFLINE_CACHE_VALUE = env.EXPO_PUBLIC_SKIP_OFFLINE_CACHE || readRuntimeConfigValue('EXPO_PUBLIC_SKIP_OFFLINE_CACHE');
  EXPO_PUBLIC_ENABLE_DETOX_CONTROLS_VALUE = env.EXPO_PUBLIC_ENABLE_DETOX_CONTROLS || readRuntimeConfigValue('EXPO_PUBLIC_ENABLE_DETOX_CONTROLS');
  EXPO_PUBLIC_MOCK_NATIVE_SHARING_VALUE = env.EXPO_PUBLIC_MOCK_NATIVE_SHARING || readRuntimeConfigValue('EXPO_PUBLIC_MOCK_NATIVE_SHARING');
  GOOGLE_PLACES_API_KEY_VALUE = env.GOOGLE_PLACES_API_KEY || readRuntimeConfigValue('GOOGLE_PLACES_API_KEY');
} catch {
  // Fallback to process.env for web builds
  EXPO_PUBLIC_ENV_VALUE = readRuntimeConfigValue('EXPO_PUBLIC_ENV');
  EXPO_PUBLIC_API_URL_VALUE = readRuntimeConfigValue('EXPO_PUBLIC_API_URL');
  EXPO_PUBLIC_SKIP_OFFLINE_CACHE_VALUE = readRuntimeConfigValue('EXPO_PUBLIC_SKIP_OFFLINE_CACHE');
  EXPO_PUBLIC_ENABLE_DETOX_CONTROLS_VALUE = readRuntimeConfigValue('EXPO_PUBLIC_ENABLE_DETOX_CONTROLS');
  EXPO_PUBLIC_MOCK_NATIVE_SHARING_VALUE = readRuntimeConfigValue('EXPO_PUBLIC_MOCK_NATIVE_SHARING');
  GOOGLE_PLACES_API_KEY_VALUE = readRuntimeConfigValue('GOOGLE_PLACES_API_KEY');
}

export interface EnvironmentConfig {
  name: string;
  apiUrl: string;
  googlePlacesApiKey: string;
  isDevelopment: boolean;
}

export const environments: Record<string, EnvironmentConfig> = {
  local: {
    name: 'Local Development',
    apiUrl: 'http://localhost:3000/api',
    googlePlacesApiKey: GOOGLE_PLACES_API_KEY_VALUE || '',
    isDevelopment: true,
  },
  dev: {
    name: 'Development',
    apiUrl: 'https://exhalehomereview-api-dev.alexmazure.com/api',
    googlePlacesApiKey: GOOGLE_PLACES_API_KEY_VALUE || '',
    isDevelopment: true,
  },
  testing: {
    name: 'Testing',
    apiUrl: 'https://exhalehomereview-api-testing.alexmazure.com/api',
    googlePlacesApiKey: GOOGLE_PLACES_API_KEY_VALUE || '',
    isDevelopment: false,
  },
  detox: {
    name: 'Detox Local Simulator',
    apiUrl: 'http://localhost:3001/api',
    googlePlacesApiKey: GOOGLE_PLACES_API_KEY_VALUE || '',
    isDevelopment: true,
  },
  production: {
    name: 'Production',
    apiUrl: 'https://exhalehomereview-api.alexmazure.com/api',
    googlePlacesApiKey: GOOGLE_PLACES_API_KEY_VALUE || '',
    isDevelopment: false,
  },
};

// Get current environment based on NODE_ENV and EXPO_PUBLIC_ENV
export function getCurrentEnvironment(): EnvironmentConfig {
  // Explicit API URL overrides all named environments. Simulator tests rely on
  // this to prevent accidental calls to remote dev/testing/production APIs.
  const explicitApiUrl = EXPO_PUBLIC_API_URL_VALUE || process.env.EXPO_PUBLIC_API_URL;
  if (explicitApiUrl) {
    const matchingEnv = Object.values(environments).find(env => env.apiUrl === explicitApiUrl);
    if (matchingEnv) {
      return matchingEnv;
    }

    return {
      name: 'Custom API',
      apiUrl: explicitApiUrl,
      googlePlacesApiKey: GOOGLE_PLACES_API_KEY_VALUE || process.env.GOOGLE_PLACES_API_KEY || '',
      isDevelopment: explicitApiUrl.includes('localhost') || explicitApiUrl.includes('127.0.0.1'),
    };
  }

  // Check for explicit environment override (from @env module or process.env)
  const envOverride = EXPO_PUBLIC_ENV_VALUE;

  if (envOverride && environments[envOverride]) {
    return environments[envOverride];
  }

  // Check if we're in local development
  if (process.env.NODE_ENV === 'development') {
    return environments.local;
  }

  // Default to production
  return environments.production;
}

export const currentEnvironment = getCurrentEnvironment();

const skipOfflineCacheValue =
  EXPO_PUBLIC_SKIP_OFFLINE_CACHE_VALUE || process.env.EXPO_PUBLIC_SKIP_OFFLINE_CACHE;

const isTruthyEnvValue = (value: string | undefined) =>
  value === '1' || value === 'true';

export const isDetoxEnvironment = currentEnvironment.name === 'Detox Local Simulator';

export const shouldSkipOfflineCache =
  skipOfflineCacheValue !== undefined
    ? isTruthyEnvValue(skipOfflineCacheValue)
    : isDetoxEnvironment;

export const isDetoxTestControlsEnabled =
  isDetoxEnvironment &&
  isTruthyEnvValue(
    EXPO_PUBLIC_ENABLE_DETOX_CONTROLS_VALUE ||
    process.env.EXPO_PUBLIC_ENABLE_DETOX_CONTROLS
  );

export const shouldMockNativeSharing =
  isDetoxEnvironment &&
  isTruthyEnvValue(
    EXPO_PUBLIC_MOCK_NATIVE_SHARING_VALUE ||
    process.env.EXPO_PUBLIC_MOCK_NATIVE_SHARING
  );
